import { Descriptions, Divider, DrawerProps, Typography } from 'antd';
import { Avatar, CommonStatus, Drawer } from 'components/common';
import { shiftsMessages, messages } from 'messages';
import { useEffect } from 'react';
import { useGetShiftDetailQuery } from 'services';
import { twMerge } from 'tailwind-merge';
import ShiftActions from './ShiftActions';
import { ShiftDto } from 'types';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from 'utils';

type ShiftDrawerDrawerProps = DrawerProps & {
  shiftId?: number;
  onChangeLoading?: (value: boolean) => void;
  onDelete?: (shift: ShiftDto) => void;
  onUpdateStatus?: (shift: ShiftDto) => void;
  onUpdateInfo?: (shift: ShiftDto) => void;
};

const ShiftDrawer = ({
  shiftId,
  onChangeLoading,
  onDelete,
  onUpdateStatus,
  onUpdateInfo,
  ...props
}: ShiftDrawerDrawerProps) => {
  const {
    data,
    isLoading: isLoadingDetail,
    isFetching: isFetchingDetail
  } = useGetShiftDetailQuery(shiftId!, {
    skip: !shiftId,
    refetchOnMountOrArgChange: true
  });
  const shift = data?.data;
  const isLoading = isFetchingDetail || isLoadingDetail;

  useEffect(() => {
    if (onChangeLoading) {
      onChangeLoading(isLoading);
    }
  }, [onChangeLoading, isLoading]);

  const descriptionItem = [
    {
      key: 'name',
      label: shiftsMessages.name,
      children: shift?.name
    },
    {
      key: 'code',
      label: shiftsMessages.code,
      children: shift?.code
    },
    {
      key: 'icon',
      label: shiftsMessages.icon,
      children: (
        <div className='flex h-6 w-6 items-center justify-center rounded-base bg-backgroundColor3'>
          <Avatar
            className='rounded-none border-none bg-none object-contain'
            size={16}
            fileId={shift?.icon}
            shape='square'
          />
        </div>
      )
    },
    {
      key: 'status',
      label: messages.status,
      children: shift && <CommonStatus textClassname='text-base' value={shift?.isActive} />
    },
    {
      key: 'priority',
      label: shiftsMessages.priority,
      children: shift?.priority
    },
    {
      key: 'workdays',
      label: shiftsMessages.workdays,
      children: shift?.workdays
    }
  ];
  const timeItem = [
    {
      key: 'fromTime',
      label: shiftsMessages.fromTime,
      children: shift?.fromTime ? `${shift.fromTime}` : '-'
    },
    {
      key: 'toTime',
      label: shiftsMessages.toTime,
      children: shift?.toTime ? `${shift.toTime}` : '-'
    }
  ];

  const descriptionHandleBy = [
    {
      key: 'updatedAt',
      label: messages.updatedAt,
      children: shift?.updatedAt && shift?.updatedByUser ? dayjs(shift.updatedAt).format(DATE_TIME_FORMAT) : '-'
    },
    {
      key: 'updatedBy',
      label: messages.updatedByUser,
      children: shift?.updatedByUser ? (
        <div className='flex gap-2'>
          <Avatar fileId={shift?.updatedByUser?.avatar} name={shift?.updatedByUser?.fullName} />
          <Typography.Text>{shift?.updatedByUser?.fullName}</Typography.Text>
        </div>
      ) : (
        '-'
      )
    },
    {
      key: 'createdAt',
      label: messages.createdAt,
      children: shift?.createdAt ? dayjs(shift.createdAt).format(DATE_TIME_FORMAT) : '-'
    },
    {
      key: 'createdBy',
      label: messages.createdByUser,
      children: shift?.createdByUser ? (
        <div className='flex gap-2'>
          <Avatar fileId={shift?.createdByUser?.avatar} name={shift?.createdByUser?.fullName} />
          <Typography.Text>{shift?.createdByUser?.fullName}</Typography.Text>
        </div>
      ) : (
        '-'
      )
    }
  ];

  return (
    <Drawer
      {...props}
      width={454}
      title={''}
      extra={
        <div>
          {shift && (
            <ShiftActions
              shift={shift}
              onDelete={onDelete}
              onUpdateStatus={onUpdateStatus}
              onUpdateInfo={onUpdateInfo}
            />
          )}
        </div>
      }
      loading={isLoading}
    >
      <div className={twMerge('min-h-9 w-full', isLoading && !shift && 'skeleton-active')}>
        <Typography.Title className='text-2.5xl'>{shift?.name}</Typography.Title>
      </div>
      <div className={twMerge('mt-8 min-h-[124px] w-full', isLoading && !shift && 'skeleton-active')}>
        <Typography.Title className='mb-4 text-lg'>{messages.general}</Typography.Title>
        <Descriptions items={descriptionItem} column={1} />
      </div>
      <Divider className='mb-8 mt-2' />
      <div className='pb-5'>
        <Typography.Title className='mb-4 text-lg'>{shiftsMessages.time}</Typography.Title>
        <Descriptions items={timeItem} column={1} />
      </div>
      <Divider className='mb-8 mt-6' />

      <Typography.Title className='mb-4 mt-0 text-lg'>{messages.handleByInfo}</Typography.Title>
      <Descriptions items={descriptionHandleBy} column={1} />
    </Drawer>
  );
};

export default ShiftDrawer;
